import { Button, Collapse, Divider, Spin, Tooltip, Typography } from "antd-v5";
import classNames from "classnames";
import AuthorisedContainer from "components/AuthorisedContainer";
import FindingRiskScoreTag from "components/FindingRiskScorePopover";
import { LoadingIcon } from "components/Icons";
import Vocabulary from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import { useAATIScoreColumn } from "hooks/useAATIScoreColumn";
import { useAssetCloudResourceFields } from "hooks/useAssetCloudResourceFields";
import { useAssetDetailOverviewFields } from "hooks/useAssetFixedFields";
import { useAssetHostFields } from "hooks/useAssetHostFields";
import { useAssetImageFields } from "hooks/useAssetImageFields";
import { useAssetNetworkResourceFields } from "hooks/useAssetNetworkResourceFields";
import { useAssetOtherTypeFields } from "hooks/useAssetOtherTypeFields";
import { useAssetRepositoryFields } from "hooks/useAssetRepositoryFields";
import { useFindingAssetCorrelationFields } from "hooks/useFindingAssetCorrelationFields";
import useResolvedFindingStatus from "hooks/useResolvedFindingStatus";
import _, { capitalize, groupBy, isEmpty } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { FindingSummary } from "types/api/findingSummary";
import { extractCVSSVersion } from "utils/cvssUtil";
import {
  getFindingDetailsSidebarCollapsedKeys,
  getFingModalPinnedFields,
  setFindingDetailsSidebarCollapsedKeys,
  setFingModalPinnedFields,
} from "utils/localStorageUtils";
import {
  RiskScoreDisableTag,
  getSeverityByBaseScore,
} from "utils/riskScoreUtil";
import { RenderExploitedIcon } from "views/Feeds/FeedDetailsModal";
import { FINDING_ACTIONS, FINDING_STATUS } from "views/Findings/Constants";
import { EPSSReceiveInterceptor } from "views/Findings/EPSSScore";
import {
  ASSET_TYPE_FIELD_DISPLAY_MAP,
  ASSET_TYPE_VALUES,
} from "views/Inventory/Assets/constants";

import ACDetailIcon from "ac-components/ac-icons/ACDetailIcon";
import ACRiskIcon from "ac-components/ac-icons/ACRiskIcon";
import ACScopeIcon from "ac-components/ac-icons/ACScopeIcon";
import CalendarIcon from "ac-components/ac-icons/CalendarIcon";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import ChevronUpIcon from "ac-components/ac-icons/ChevronUpIcon";
import EditIcon from "ac-components/ac-icons/EditIcon";
import PinIcon from "ac-components/ac-icons/PinIcon";
import ServerIcon from "ac-components/ac-icons/ServerIcon";
import TeamsIcon from "ac-components/ac-icons/TeamsIcon";
import UnpinIcon from "ac-components/ac-icons/UnpinIcon";
import ACVulnerabilityIcon from "ac-components/ac-icons/VulnerabilityIcon";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACRiskScoreTag from "ac-components/ac-riskscore-tag/ACRiskScoreTag";

import { FindingDetailsGrid } from "../FindingDetailsGrid";
import { useFindingDetails } from "../FindingDetailsProvider";
import { getHeader, getList } from "../FindingDetailsSourceInfo";
import Attachments, { AttachmentHeader } from "../ModalV3/Attachments";
import FindingPentestDetails from "../ModalV3/FindingPentest";
import FindingTimeline from "../ModalV3/FindingTimeline";
import { RiskTagsMiniDisplay } from "../RiskTagsDisplay";
import {
  AATIScoreField,
  AMCRuleField,
  AssetScoreExtraField,
  AssetScoreField,
  BusinessOwnerField,
  CISAKEVField,
  CTIScoreField,
  CVEField,
  CVELastModifiedDateField,
  CVEPublishedDateField,
  CVSSBaseScoreField,
  CVSSVectorField,
  CWEField,
  CategoryField,
  CompainceOwnerField,
  ComplianceField,
  EngagementField,
  EngineeringOwnerField,
  EnvField,
  ExploitabilityScoreField,
  ExploitedField,
  FindingScoreField,
  FoundOnField,
  GSCCExploitationActivityField,
  GSCCObservedInTheWildField,
  GSCCUpstreamFixAvailableField,
  GSCCZeroDayField,
  ImpactScoreField,
  LastAcceptRiskField,
  LastChangedField,
  LastConfirmedField,
  LastFalsePositiveField,
  LastMitigatedField,
  LastOpenedField,
  LastSeenField,
  LastSuppressedField,
  LastTriagedField,
  LatestToolScanDateField,
  OwnerField,
  ProductField,
  RemediationField,
  SLADueOnField,
  SecurityOwnerField,
  SeverityField,
  StatusField,
  SubProductField,
  SupportOwnerField,
  TeamField,
  ThreatSourceField,
  TriageField,
  getSLAFieldStatusIcon,
  isGsccEnrichedFinding,
} from "./FieldDefinitions";
import {
  PinnableFieldSectionTypes,
  PinnableFieldTypes,
} from "./PinnableFieldTypes";

export default function PinnableFields() {
  const {
    history,
    finding,
    loading,
    isCustomFinding,
    openFindingFieldEditPopup,
    showCVSSVectorComponents,
    showDueDatePopup,
    showFindingActionPopup,
    handleArmorcodeSeverityChange,
    handleArmorcodeCategoryChange,
    cveDetails,
    // @ts-expect-error TODO: define type for useFindingDetails
    assetDetails,
    showBasicEdit,
    // @ts-expect-error TODO: define type for useFindingDetails
    ctiScoreCveDetails,
  } = useFindingDetails();

  const featureFlags = useFeatureFlag();
  const { getAATIScoreWithPopover } = useAATIScoreColumn();
  const resolvedStatus = useResolvedFindingStatus();
  const globalSettings = useGlobalSettingsContext();
  const [pinnedFields, setPinnedFields] = useState<string[]>(() =>
    getFingModalPinnedFields(),
  );
  const [defaultActiveKey] = useState<string[]>(() => {
    const _defaultActiveKey = ["pinned", "scope", "riskScore", "general"];
    if (_.isEmpty(pinnedFields)) {
      _defaultActiveKey.shift();
    }
    return _defaultActiveKey;
  });

  const [activekeys, setActivekeys] = useState(() =>
    getFindingDetailsSidebarCollapsedKeys(defaultActiveKey),
  );

  // If the finding is resolved i.e Mitigated, remediated, false+ve, accept risk or severity is INFO, we should not show any calculation logic tooltip, the scores may not match with the current state of the finding
  const isStatusResolvedOrInfoSeverity =
    // @ts-expect-error TODO: define type for useFindingDetails
    resolvedStatus.includes(finding?.status) || finding?.severity === "INFO";

  const riskScoreTag = !isStatusResolvedOrInfoSeverity ? (
    // TODO: define type for FindingRiskScoreTag
    // @ts-expect-error
    <FindingRiskScoreTag finding={finding} placement="topRight">
      {/* TODO: define type for useFindingDetails */}
      {/* @ts-expect-error */}
      <ACRiskScoreTag value={finding?.riskScore} />
    </FindingRiskScoreTag>
  ) : (
    // @ts-expect-error TODO: define type for useFindingDetails
    <ACRiskScoreTag value={finding?.riskScore} />
  );

  const riskScore = featureFlags.RISK_SCORE_3_0 ? (
    loading ? (
      <LoadingIcon />
    ) : globalSettings.riskScoreEnabled ? (
      riskScoreTag
    ) : (
      // @ts-expect-error TODO: define type for RiskScoreDisableTag
      <RiskScoreDisableTag history={history} />
    )
  ) : null;

  const onScopeEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    showBasicEdit();
  };

  // @ts-expect-error TODO: define type for useFindingDetails
  const isExploited = _.get(finding?.additionalDetails, "exploited");

  const sections: PinnableFieldSectionTypes[] = [
    {
      name: "pinned",
      header: (
        <div className="header-title">
          <PinIcon />
          Pinned
        </div>
      ),
      noData: "No Pinned Fields",
    },
    {
      name: "scope",
      header: (
        <div className="header-title">
          <ACScopeIcon />
          Scope{" "}
          <AuthorisedContainer
            permission={PermissionSetMap.FINDINGS_UPDATE_BASIC_DETAILS}
            tooltip={
              <>
                Edit <Vocabulary name="product" />,{" "}
                <Vocabulary name="subProduct" /> and Environment
              </>
            }
            placement={"bottom"}
          >
            <Button size="small" type="text" onClick={onScopeEditClick}>
              <EditIcon />
            </Button>
          </AuthorisedContainer>
        </div>
      ),
    },
    {
      name: "riskScore",
      header: (
        <div className="header-title d-flex justify-content-between align-items-center w-100">
          {/* Left: Icon + Risk label */}
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <ACRiskIcon />
            <span>Risk</span>
          </div>
          {/* Right: Risk score + mini icons (always right-aligned) */}
          <div className="d-flex align-items-center">
            {riskScore}
            {/* Only show mini icons when section is collapsed */}
            {!activekeys.includes("riskScore") && (
              <>
                {/* @ts-expect-error TODO: define type for finding */}
                <RiskTagsMiniDisplay tags={finding?.tagsUsedForAssetScore} />
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      name: "general",
      header: (
        <div className="header-title">
          <ACDetailIcon />
          Finding Details
        </div>
      ),
    },
    ...(_.get(finding, "assessments[0].id")
      ? [
          {
            name: "assessments",
            header: (
              <div className="header-title">
                <Vocabulary name="assessment" /> Details
              </div>
            ),
            render: () => {
              return <FindingPentestDetails finding={finding} />;
            },
          },
        ]
      : []),
    {
      name: "tool",
      header: getHeader(finding, loading, true, openFindingFieldEditPopup),
    },
    ...(featureFlags.RBVM_ASSETS && !isEmpty(assetDetails)
      ? [
          {
            name: "asset",
            header: (
              <div className="header-title">
                <ServerIcon />
                Asset Details
              </div>
            ),
          },
        ]
      : []),
    {
      name: "vulnerability",
      header: (
        <div className="header-title">
          <ACVulnerabilityIcon />
          Vulnerability
          {isExploited && <RenderExploitedIcon className="m-l-xs" />}
        </div>
      ),
    },
    {
      name: "timeline",
      header: (
        <div className="header-title">
          <CalendarIcon /> Timeline
        </div>
      ),
      render: (props) => {
        return <FindingTimeline finding={finding} {...props} />;
      },
    },
    {
      name: "owner",
      header: (
        <div className="header-title">
          <TeamsIcon /> Team
        </div>
      ),
    },
    {
      name: "attachment",
      header: <AttachmentHeader />,
      render: () => {
        return <Attachments />;
      },
    },
  ];

  const onPinFieldClick = useCallback((field, isPinned) => {
    setPinnedFields((prev) => {
      let fields;
      const selectedFields = [getFieldName(field)!];
      if (!_.isEmpty(field.relatedFields)) {
        selectedFields.push(...field.relatedFields);
      }
      if (isPinned) {
        fields = prev.filter(
          (fieldName) => !selectedFields.includes(fieldName),
        );
      } else {
        fields = [...prev, ...selectedFields];
      }
      setFingModalPinnedFields(fields);
      return fields;
    });
  }, []);

  const assetFixedFields = useAssetDetailOverviewFields(assetDetails);
  const assetHostFields = useAssetHostFields(assetDetails);
  const assetImageFields = useAssetImageFields(assetDetails);
  const assetCloudResourceFields = useAssetCloudResourceFields(assetDetails);
  const assetRepositoryFields = useAssetRepositoryFields(assetDetails);
  const assetOtherTypeFields = useAssetOtherTypeFields(assetDetails);
  const assetNetworkResourceFields =
    useAssetNetworkResourceFields(assetDetails);
  const assetCorrelationFields = useFindingAssetCorrelationFields({
    finding,
    assetName: assetDetails?.name,
  });

  const assetDetailsField: PinnableFieldTypes[] = useMemo(() => {
    const TYPE_FIELD_MAP = {
      [ASSET_TYPE_VALUES.HOST]: assetHostFields,
      [ASSET_TYPE_VALUES.IMAGE]: assetImageFields,
      [ASSET_TYPE_VALUES.CLOUD_RESOURCE]: assetCloudResourceFields,
      [ASSET_TYPE_VALUES.REPOSITORY]: assetRepositoryFields,
      [ASSET_TYPE_VALUES.OTHERS]: assetOtherTypeFields,
      [ASSET_TYPE_VALUES.NETWORK_RESOURCE]: assetNetworkResourceFields,
    };
    const assetFields = [
      ...assetFixedFields,
      ...(TYPE_FIELD_MAP[
        assetDetails.armorCodeType as keyof typeof TYPE_FIELD_MAP
      ]
        ? [
            {
              label: "subSection",
              name: "subSection",
              path: "subSection",
              span: 24,
              group: "asset",
              formatter: () => {
                return (
                  <>
                    <Divider type="horizontal" />
                    <div style={{ marginLeft: 4 }}>
                      <strong>
                        {ASSET_TYPE_FIELD_DISPLAY_MAP[
                          assetDetails.armorCodeType as keyof typeof ASSET_TYPE_FIELD_DISPLAY_MAP
                        ] ?? capitalize(assetDetails.armorCodeType)}{" "}
                        Details
                      </strong>
                    </div>
                  </>
                );
              },
            },
            ...TYPE_FIELD_MAP[
              assetDetails.armorCodeType as keyof typeof TYPE_FIELD_MAP
            ],
          ]
        : []),
    ];
    return assetFields.map((asset) => ({
      group: "asset",
      span: 24,
      path: "asset",
      ...asset,
    }));
  }, [
    assetHostFields,
    assetImageFields,
    assetCloudResourceFields,
    assetRepositoryFields,
    assetOtherTypeFields,
    assetFixedFields,
    assetDetails.armorCodeType,
    assetNetworkResourceFields,
  ]);

  const groupedFields = useMemo(() => {
    const AATIExtraField = {
      label: "",
      path: "additionalDetails.aatiScore",
      name: "aatiScoreExtras",
      group: "vulnerability",
      span: 24,
      valueClassName: "break-content",
      formatter: () => {
        if (cveDetails) {
          // @ts-expect-error TODO: defind type for useFIndingDetails -> cveDetails
          const poc = _.get(cveDetails.threatTags, "proof of concept");
          // @ts-expect-error TODO: defind type for useFIndingDetails -> cveDetails
          const itw = _.get(cveDetails.threatTags, "in the wild");
          // @ts-expect-error TODO: defind type for useFIndingDetails -> cveDetails
          const pf = _.get(cveDetails.threatTags, "pentester framework");
          // @ts-expect-error TODO: defind type for useFIndingDetails -> cveDetails
          const cs = _.get(cveDetails.threatTags, "code snippet");
          const values = [];
          if (poc) {
            values.push(`PoC(${poc})`);
          }
          if (itw) {
            values.push(`in the wild(${itw})`);
          }
          if (pf) {
            values.push(`Pentester Framework(${pf})`);
          }
          if (cs) {
            values.push(`Code Snippet(${cs ? "Y" : "N"})`);
          }
          // @ts-expect-error TODO: defind type for useFIndingDetails
          if (!_.isNil(cveDetails.otherProperties?.epssProbability)) {
            values.push(
              // @ts-expect-error TODO: defind type for useFIndingDetails
              `EPSS Score: ${EPSSReceiveInterceptor([cveDetails.otherProperties?.epssProbability])}`,
            );
          }
          // @ts-expect-error TODO: defind type for useFIndingDetails
          if (!_.isNil(cveDetails.epssScore)) {
            values.push(
              // @ts-expect-error TODO: defind type for useFIndingDetails
              `EPSS Percentile: ${EPSSReceiveInterceptor([cveDetails.epssScore])}`,
            );
          }
          return <div className="secondary-values">{values.join(", ")}</div>;
        }
      },
    };
    const FindingScoreExtraField = {
      label: "",
      path: "findingScore",
      name: "findingScoreExtras",
      group: "riskScore",
      span: 24,
      formatter: (_AS: unknown, finding: FindingSummary) => {
        const aatiScore = _.get(finding, "additionalDetails.aatiScore");
        const severity = _.capitalize(getSeverityByBaseScore(aatiScore));
        return (
          <div className="secondary-values">
            AATI: {getAATIScoreWithPopover(aatiScore, cveDetails)} - {severity}
          </div>
        );
      },
    };

    const CTIScoreExtraField = {
      label: "",
      path: "additionalDetails.ctiScore",
      name: "ctiScoreExtras",
      group: "vulnerability",
      span: 24,
      valueClassName: "break-content",
      formatter: () => {
        if (ctiScoreCveDetails) {
          const riskRating = _.get(
            ctiScoreCveDetails.otherProperties,
            "risk_rating",
          );
          const exploitationState = _.get(
            ctiScoreCveDetails.otherProperties,
            "exploitationState",
          );
          const wasZeroDay = _.get(ctiScoreCveDetails.threatTags, "wasZeroDay");
          const observedInWild = _.get(
            ctiScoreCveDetails.threatTags,
            "observedInTheWild",
          );
          const mitigationStatus = _.get(
            ctiScoreCveDetails.otherProperties,
            "mitigationStatus",
          );
          const values = [];
          if (riskRating) {
            values.push(`Risk Rating(${riskRating})`);
          }
          if (!_.isNil(wasZeroDay)) {
            values.push(`Exploited as Zero-day(${wasZeroDay})`);
          }
          if (exploitationState) {
            values.push(`Exploitation State(${exploitationState})`);
          }
          if (!_.isNil(observedInWild)) {
            values.push(`Exploited in the wild(${observedInWild})`);
          }
          if (mitigationStatus) {
            values.push(`Mitigation Status(${mitigationStatus})`);
          }
          return <div className="secondary-values">{values.join(", ")}</div>;
        }
      },
    };

    const _findingScoreField = {
      ...FindingScoreField,
      relatedFields: ["findingScoreExtras"],
    };

    const _AATIScoreField = {
      ...AATIScoreField,
      formatter: (score: number, finding: FindingSummary) =>
        AATIScoreField.formatter!(
          getAATIScoreWithPopover(score, cveDetails),
          finding,
        ),
      relatedFields: ["aatiScoreExtras"],
    };

    const _ctiScoreField = {
      ...CTIScoreField,
      relatedFields: ["CTIScoreExtraField"],
    };

    const fields: PinnableFieldTypes[] = [
      {
        ...SeverityField,
        formatter: (severity: string, finding: FindingSummary) =>
          SeverityField.formatter!(
            severity,
            finding,
            handleArmorcodeSeverityChange,
          ),
        span: 24,
      },
      {
        ...CategoryField,
        formatter: (category: string, finding: FindingSummary) =>
          CategoryField.formatter!(
            category,
            finding,
            handleArmorcodeCategoryChange,
          ),
      },
      _findingScoreField,
      ...(featureFlags.AATI_SCORE_COLUMN ? [FindingScoreExtraField] : []),
      StatusField,
      EngagementField,
      ComplianceField,
      ...(
        getList(
          loading,
          finding,
          openFindingFieldEditPopup,
          featureFlags,
        ) as PinnableFieldTypes[]
      ).map((i) => ({
        ...i,
        group: "tool",
        span: 24,
      })),
      {
        ...AMCRuleField,
        editAction: isCustomFinding
          ? () => openFindingFieldEditPopup("ruleKey")
          : undefined,
      },
      ...(featureFlags.AATI_SCORE_COLUMN
        ? [_AATIScoreField, AATIExtraField]
        : []),
      _ctiScoreField,
      CTIScoreExtraField,
      ThreatSourceField,
      ...(isGsccEnrichedFinding(finding)
        ? [
            GSCCObservedInTheWildField,
            GSCCExploitationActivityField,
            GSCCZeroDayField,
            GSCCUpstreamFixAvailableField,
          ]
        : []),
      {
        ...ExploitedField,
        editAction: isCustomFinding
          ? () => openFindingFieldEditPopup("exploited")
          : undefined,
      },
      CISAKEVField,
      {
        ...CVSSBaseScoreField,
        editAction: isCustomFinding
          ? () => openFindingFieldEditPopup("cvssScore")
          : undefined,
      },
      {
        ...CVSSVectorField,
        span: 24,
        //Enable CVSS Vcector action for findings with 3.1 version irrespective of the sourcei
        editAction:
          isCustomFinding ||
          // @ts-expect-error TODO: define type for useFindingDetails
          extractCVSSVersion(finding?.cvssVector) === "3.1"
            ? () => openFindingFieldEditPopup("cvssVector")
            : undefined,
        formatter: (vector: string, finding: FindingSummary) =>
          CVSSVectorField.formatter!(vector, finding, showCVSSVectorComponents),
      },
      ImpactScoreField,
      ExploitabilityScoreField,
      {
        ...CVEField,
        editAction: isCustomFinding
          ? () => openFindingFieldEditPopup("cves")
          : undefined,
      },
      CVEPublishedDateField,
      CVELastModifiedDateField,
      {
        ...CWEField,
        editAction: () => openFindingFieldEditPopup("cwes"),
      },
      FoundOnField,
      {
        ...SLADueOnField,
        ...(globalSettings.isSLAEnabled
          ? {
              icon: (data: FindingSummary) =>
                getSLAFieldStatusIcon(data, "resolutionSlaStatus"),
              editAction: () => showDueDatePopup(),
              formatter: (value, finding) =>
                SLADueOnField.formatter!(value, finding),
            }
          : { formatter: () => NOT_AVAILABLE }),
      },
      {
        ...TriageField,
        formatter: (value, finding) =>
          globalSettings.isSLAEnabled
            ? TriageField.formatter!(value, finding)
            : NOT_AVAILABLE,
      },
      {
        ...RemediationField,
        formatter: (value, finding) =>
          globalSettings.isSLAEnabled
            ? RemediationField.formatter!(value, finding)
            : NOT_AVAILABLE,
      },
      LastSeenField,
      LastChangedField,
      LastMitigatedField,
      ...(globalSettings.disabledFindingStatus.includes(FINDING_STATUS.TRIAGE)
        ? []
        : [LastTriagedField]),
      LastConfirmedField,
      LastOpenedField,
      LastAcceptRiskField,
      LastSuppressedField,
      LastFalsePositiveField,
      LatestToolScanDateField,
      {
        ...OwnerField,
        editAction: () => showFindingActionPopup(FINDING_ACTIONS.CHANGE_OWNER),
      },
      TeamField,
      ...(_.get(finding, "businessOwner.name") ? [BusinessOwnerField] : []),
      ...(_.get(finding, "securityOwner.name") ? [SecurityOwnerField] : []),
      ...(_.get(finding, "engineeringOwner.name")
        ? [EngineeringOwnerField]
        : []),
      ...(_.get(finding, "supportOwner.name") ? [SupportOwnerField] : []),
      ...(_.get(finding, "complianceOwner.name") ? [CompainceOwnerField] : []),
      ...(featureFlags.RISK_SCORE_3_0
        ? [AssetScoreField, AssetScoreExtraField]
        : []),
      ...assetDetailsField,
      ...assetCorrelationFields,
      ProductField,
      SubProductField,
      EnvField,
      // {
      //   ...EnvField,
      //   formatter: (environmentName) => (
      //     <FilterableCell filterKey={EnvField.name} value={EnvField.name}>
      //         <EnvironmentName name={environmentName} />
      //     </FilterableCell>
      // )
      // },
    ];

    // Adding the pin information to the fields
    const _fields = fields.map((field, i) => {
      const isFieldPinned = pinnedFields.includes(getFieldName(field)!);
      const Icon = isFieldPinned ? UnpinIcon : PinIcon;
      const title = isFieldPinned ? "Unpin Field" : "Pin Field";
      // Ensure label is always string or React.ReactElement
      const labelValue =
        typeof field.label === "function" ? field.label() : field.label;
      const labelProps: { title: string } = { title: "" };
      if (typeof labelValue === "string") {
        labelProps.title = labelValue;
      }
      return {
        ...field,
        label: field.label ? (
          <div
            key={i}
            className="label-content-wrapper d-flex align-items-flex-start"
          >
            <span className="label-content" {...labelProps}>
              {labelValue}
            </span>
            {!field.disabledPin ? (
              <Tooltip
                title={title}
                placement="right"
                key={`${getFieldName(field)}-${isFieldPinned}`}
              >
                <Icon
                  style={{ fontSize: "16px" }}
                  className={classNames(
                    "d-flex align-items-center m-l-xs clickable",
                    { "pin-icon": !isFieldPinned },
                  )}
                  onClick={() => onPinFieldClick(field, isFieldPinned)}
                />
              </Tooltip>
            ) : (
              ""
            )}
          </div>
        ) : (
          labelValue
        ),
        group: field.group,
        isPinned: isFieldPinned,
      };
    });

    const groupByFields = groupBy(_fields, "group");
    const pinned = _.filter(_fields, { isPinned: true });
    groupByFields.pinned = pinned;
    // Ordering the pinned fileds as per the user selection order
    if (groupByFields.pinned) {
      // Filter the fields which are pinned but not available in the findings popup
      groupByFields.pinned = _.compact(
        pinnedFields.map((fieldName) => {
          return groupByFields.pinned.find(
            (field) => getFieldName(field) === fieldName,
          );
        }),
      );
    }

    return groupByFields;
  }, [
    featureFlags,
    handleArmorcodeCategoryChange,
    handleArmorcodeSeverityChange,
    isCustomFinding,
    openFindingFieldEditPopup,
    showCVSSVectorComponents,
    showDueDatePopup,
    showFindingActionPopup,
    cveDetails,
    pinnedFields,
    onPinFieldClick,
    finding,
    loading,
    assetDetailsField,
    assetCorrelationFields,
    globalSettings.disabledFindingStatus,
    globalSettings.isSLAEnabled,
    ctiScoreCveDetails,
    getAATIScoreWithPopover,
  ]);

  const handleCollapseChange = (keys: string | string[]) => {
    const keysArray = keys! as string[];
    setFindingDetailsSidebarCollapsedKeys(keysArray);
    setActivekeys(keysArray);
  };
  const handleCollapsedIcons = (expanded: boolean) =>
    expanded ? (
      <ChevronUpIcon className="font-size-24" />
    ) : (
      <ChevronDownIcon className="font-size-24" />
    );
  return (
    <Collapse
      bordered={false}
      defaultActiveKey={activekeys}
      expandIcon={({ isActive }) => handleCollapsedIcons(isActive || false)}
      expandIconPosition="end"
      onChange={handleCollapseChange}
      items={sections.map(({ name, header, noData, render }) => {
        return {
          label: <span className="text-section-subtitle">{header}</span>,
          key: name,
          children: (
            <React.Fragment key={name}>
              {/* {i !== 0 ? <div className="p-b-sm" /> : null} */}
              <Spin spinning={loading}>
                {render ? (
                  render({ fields: groupedFields[name] })
                ) : !_.isEmpty(groupedFields[name]) ? (
                  <FindingDetailsGrid
                    list={groupedFields[name] || []}
                    data={finding}
                    labelWidth={120}
                    itemGapClassName=""
                    valueContainerClassName="flex-1"
                  />
                ) : (
                  <Typography.Paragraph
                    className="text-center m-b-0"
                    type="secondary"
                  >
                    {noData || "No data"}
                  </Typography.Paragraph>
                )}
              </Spin>
            </React.Fragment>
          ),
        };
      })}
    />
  );
}

const getFieldName = (field: PinnableFieldTypes) =>
  field.pinnableFieldName || field.name;
